import { useStore } from "@mapcraft-tasks/client-store/react";
import {
	collapse,
	expand,
	mentionsOf,
	preserveLostMentions,
	remap,
	type Token,
	tokenAt,
	tokenEndingAt,
	tokenStartingAt
} from "@mapcraft-tasks/domain";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { usePhone } from "../../lib/mobile";

/**
 * Редактор строки — токенизированное поле с одним слоем плашек поверх.
 *
 * Библиотека-редактор (ProseMirror / Lexical / TipTap) намеренно НЕ берётся: она
 * окупается на абзацах и вложенных сущностях, а здесь короткий однострочный заголовок
 * с редкими меншенами. Вдобавок она приносит собственный undo, который пришлось бы
 * стыковать с клиентским стеком обратных команд (stack.md, ui-spec 8.5).
 *
 * Плашка токена рисуется вторым слоем поверх прозрачного поля: в поле лежит ровно тот
 * текст, что виден, — значит ширины совпадают.
 *
 * Поле — `textarea`, а не `input`, ЕДИНСТВЕННО ради переноса. Заголовок остаётся
 * однострочным по смыслу: `Enter` заводит соседа, перевод строки не вставляется никогда
 * (см. `handleKeyDown` и очистку в `handleChange`). Но на телефоне строка в списке
 * переносится, а `input` переноситься не умеет: при входе в правку двухстрочная строка
 * схлопывалась в одну, и каретка уезжала за край без возможности прокрутить. На десктопе
 * поведение прежнее — `wrap="off"`, горизонтальная прокрутка, высота в одну строку.
 */
export function TitleEditor({
	value,
	caret,
	onCommit,
	onCancel,
	onKeyDown,
	placeholder
}: {
	value: string;
	caret: "start" | "end" | number | null;
	onCommit: (raw: string) => void;
	onCancel: () => void;
	onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	placeholder?: string;
}) {
	const store = useStore();
	const phone = usePhone();
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const layerRef = useRef<HTMLSpanElement>(null);
	const titleOf = useCallback((id: string) => store.getSnapshot().replica.nodes.get(id)?.title, [store]);

	const initial = useMemo(() => expand(value, titleOf), [value, titleOf]);
	const [text, setText] = useState(initial.text);
	const [tokens, setTokens] = useState<Token[]>(initial.tokens);
	const removed = useRef(new Set<string>());
	const before = useMemo(() => mentionsOf({ title: value, body: null }), [value]);

	const [suggest, setSuggest] = useState<{ from: number; query: string } | null>(null);
	// Пункт под клавиатурой. Живёт здесь, а не в списке: выбирают его стрелками
	// из поля, а поле фокус не отдаёт — иначе редактор схлопнется по блюру.
	const [active, setActive] = useState(0);
	const results = useMentionResults(suggest?.query ?? null);
	const activeIndex = results.length === 0 ? 0 : Math.min(active, results.length - 1);

	useLayoutEffect(() => {
		const input = inputRef.current;
		if (!input) {
			return;
		}
		input.focus();
		const position = caret === "start" ? 0 : typeof caret === "number" ? Math.min(caret, text.length) : text.length;
		input.setSelectionRange(position, position);
		// Один раз на вход в редактор: дальше каретку двигает человек.
	}, []);

	/**
	 * Что уже дослано наверх. Выходов из редактора несколько (`Esc`, блюр, размонтирование,
	 * отступ), и они накладываются: `Tab` шлёт правку сам, а следом ту же самую дошлёт
	 * размонтирование. Вторая команда уходит со СТАРОЙ ревизией контента и возвращается
	 * отказом `stale_content` — строка получает ⚠ на ровном месте.
	 */
	const committed = useRef(value);

	const commit = useCallback(() => {
		const raw = preserveLostMentions(collapse(text, tokens), before, removed.current);
		if (raw === committed.current) {
			return;
		}
		committed.current = raw;
		onCommit(raw);
	}, [text, tokens, before, onCommit]);

	// --- правка ------------------------------------------------------------

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		// Перевод строки в заголовок не попадает даже вставкой. Пробел вместо него, а не
		// вырезание: длина сохраняется, и смещения токенов остаются верными.
		const next = event.target.value.replace(/\r?\n/g, " ");
		const survived = remap(tokens, text, next);
		for (const token of tokens) {
			if (!survived.some((t) => t.id === token.id)) {
				// Явное удаление токена: страховка не должна возвращать его обратно.
				removed.current.add(token.id);
			}
		}
		setTokens(survived);
		setText(next);

		syncSuggest(next, event.target.selectionStart ?? next.length);
	};

	/**
	 * Состояние подсказки = «что стоит между `[[` и кареткой». Считается и на правке,
	 * и на любом движении каретки: увести её стрелкой, оставив список открытым, нельзя —
	 * `from` указывал бы в прежнее место, и выбор пункта съел бы кусок заголовка.
	 *
	 * `[[` открывает поиск по живому дереву. Пункта «создать ноду» нет — `[[` только
	 * ССЫЛАЕТСЯ на существующее (ui-spec 8.4).
	 */
	const syncSuggest = (value: string, caretPos: number) => {
		const opened = value.lastIndexOf("[[", caretPos);
		const open =
			opened >= 0 && caretPos >= opened + 2 && !value.slice(opened, caretPos).includes("]]")
				? { from: opened, query: value.slice(opened + 2, caretPos) }
				: null;
		if (open?.from === suggest?.from && open?.query === suggest?.query) {
			// Ничего не изменилось. Выходим до `setActive`, иначе `keyup` от стрелки
			// сбрасывал бы выбор, только что сделанный её же `keydown`.
			return;
		}
		setSuggest(open);
		// Список пересобран под новый запрос — прежний выбор к нему не относится.
		setActive(0);
	};

	/** Курсор не встаёт внутрь токена: прыжок через него целиком. */
	const guardCaret = () => {
		const input = inputRef.current;
		if (!input) {
			return;
		}
		const start = input.selectionStart ?? 0;
		const end = input.selectionEnd ?? 0;
		if (start !== end) {
			// Выделение, задевшее часть токена, расширяется до целого.
			let lo = start;
			let hi = end;
			for (const token of tokens) {
				if (token.start < hi && token.end > lo) {
					lo = Math.min(lo, token.start);
					hi = Math.max(hi, token.end);
				}
			}
			if (lo !== start || hi !== end) {
				input.setSelectionRange(lo, hi);
			}
			return;
		}
		const inside = tokenAt(tokens, start);
		if (inside) {
			const target = start - inside.start < inside.end - start ? inside.start : inside.end;
			input.setSelectionRange(target, target);
			syncSuggest(input.value, target);
			return;
		}
		syncSuggest(input.value, start);
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		const input = inputRef.current;
		if (!input) {
			return;
		}

		// `textarea` вставил бы перевод строки на любом `Enter`, включая `Alt+Enter`,
		// который режет строку по каретке. Заголовок однострочный — гасим здесь один раз,
		// а не в каждом из вызывающих.
		if (event.key === "Enter") {
			event.preventDefault();
		}
		const start = input.selectionStart ?? 0;
		const end = input.selectionEnd ?? 0;

		// Пока открыта подсказка, стрелки, `Enter` и `Tab` принадлежат ей, а не строке:
		// иначе `Enter` заводит соседа поверх недописанного `[[`, а `Tab` уносит строку
		// в отступ. Возврат здесь обязателен — до `onKeyDown` аутлайна дойти не должно.
		if (suggest) {
			if (event.key === "Escape") {
				event.preventDefault();
				setSuggest(null);
				return;
			}
			if (results.length > 0 && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
				event.preventDefault();
				const step = event.key === "ArrowDown" ? 1 : -1;
				setActive((results.length + activeIndex + step) % results.length);
				return;
			}
			if (results.length > 0 && (event.key === "Enter" || event.key === "Tab")) {
				event.preventDefault();
				const pick = results[activeIndex]!;
				insertMention(pick.id, pick.title);
				return;
			}
		}

		// Backspace/Delete на границе сносят меншен ЦЕЛИКОМ, а не последнюю букву заголовка.
		if (event.key === "Backspace" && start === end) {
			const token = tokenEndingAt(tokens, start);
			if (token) {
				event.preventDefault();
				removed.current.add(token.id);
				const next = text.slice(0, token.start) + text.slice(token.end);
				setTokens(remap(tokens, text, next).filter((t) => t.id !== token.id));
				setText(next);
				queueMicrotask(() => input.setSelectionRange(token.start, token.start));
				return;
			}
		}
		if (event.key === "Delete" && start === end) {
			const token = tokenStartingAt(tokens, start);
			if (token) {
				event.preventDefault();
				removed.current.add(token.id);
				const next = text.slice(0, token.start) + text.slice(token.end);
				setTokens(remap(tokens, text, next).filter((t) => t.id !== token.id));
				setText(next);
				return;
			}
		}

		if (event.key === "Escape") {
			event.preventDefault();
			commit();
			onCancel();
			return;
		}

		// `Tab` уносит строку в другой контейнер. Ключ строки — путь, значит редактор
		// пересоздастся уже на новом месте и поднимет заголовок ИЗ РЕПЛИКИ: набранное надо
		// дослать ДО переезда, иначе новый редактор откроется со старым текстом и затрёт им
		// правку.
		if (event.key === "Tab") {
			commit();
		}

		onKeyDown?.(event);
	};

	const insertMention = (id: string, label: string) => {
		if (!suggest) {
			return;
		}
		const input = inputRef.current;
		const caretPos = input?.selectionStart ?? text.length;
		const next = text.slice(0, suggest.from) + label + text.slice(caretPos);
		const shifted = remap(tokens, text, next);
		setTokens([...shifted, { start: suggest.from, end: suggest.from + label.length, id, label }]);
		setText(next);
		setSuggest(null);
		queueMicrotask(() => {
			const position = suggest.from + label.length;
			input?.setSelectionRange(position, position);
			input?.focus();
		});
	};

	// Правка схлопывается дебаунсом ~500 мс, но блюр и выход обязаны дослать её сразу.
	useEffect(() => () => commitRef.current(), []);
	const commitRef = useRef(commit);
	commitRef.current = commit;

	/**
	 * Видимый слой обязан ехать вместе с полем.
	 *
	 * На десктопе поле однострочное с горизонтальной прокруткой (`wrap="off"`), и когда
	 * каретка уходит за правый край, браузер прокручивает САМО поле — а оно прозрачное.
	 * Слой с плашками лежит отдельным элементом и о прокрутке не знает: текст на экране
	 * замирает, набор продолжается вслепую, и заголовок выглядит упёршимся в предел длины.
	 * Никакого предела нет — ни в схеме, ни в базе.
	 */
	const syncScroll = useCallback(() => {
		const input = inputRef.current;
		const layer = layerRef.current;
		if (input && layer) {
			layer.scrollLeft = input.scrollLeft;
		}
	}, []);

	// После правки: слой перерисован, поле уже прокручено к каретке — сводим их до кадра.
	useLayoutEffect(syncScroll, [syncScroll, text, tokens]);

	/**
	 * Высота поля = высота текста. Считается на каждую правку, потому что перенос
	 * добавляет и убирает строки по ходу набора, а не один раз на входе.
	 *
	 * Сбросить в `auto` перед замером обязательно: `scrollHeight` не уменьшается сам,
	 * пока элементу задана прежняя высота, и поле, разъехавшееся на три строки, таким
	 * бы и осталось после удаления текста.
	 */
	useLayoutEffect(() => {
		const input = inputRef.current;
		if (!input) {
			return;
		}
		if (!phone) {
			input.style.height = "";
			return;
		}
		input.style.height = "auto";
		input.style.height = `${input.scrollHeight}px`;
	}, [text, phone]);

	return (
		// `title-editor` — крючок для мобильного правила «поле не мельче 16px»: Safari
		// зумит страницу на фокусе в поле мельче и обратно не отъезжает. Размер задаётся
		// обёртке, а не `input`: слой с плашками обязан получить тот же кегль, иначе
		// меншены разъедутся с текстом.
		<span className="title-editor relative block w-full">
			{/*
			 * Видимый слой. Работает потому, что в `input` лежит РОВНО тот текст, что виден:
			 * ширины совпадают символ в символ, и плашка ложится на своё место.
			 * Сам `input` держится прозрачным — от него нужны только каретка и выделение.
			 */}
			<span
				ref={layerRef}
				aria-hidden
				className="title-layer pointer-events-none absolute inset-0 select-none overflow-hidden"
			>
				{renderLayer(text, tokens)}
			</span>
			<textarea
				ref={inputRef}
				rows={1}
				// `off` — это и есть прежний `input`: одна строка и горизонтальная прокрутка.
				wrap={phone ? "soft" : "off"}
				value={text}
				placeholder={placeholder}
				spellCheck={false}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				// Прокрутка приходит не только от набора: стрелки, `Home`/`End`, колесо и
				// перетаскивание выделения двигают поле мимо `onChange`.
				onScroll={syncScroll}
				onKeyUp={guardCaret}
				onClick={guardCaret}
				onSelect={guardCaret}
				onBlur={() => {
					commit();
					onCancel();
				}}
				className="title-field relative block w-full bg-transparent text-transparent caret-link outline-none placeholder:text-ink-faint"
			/>
			{suggest && (
				<MentionSuggest results={results} active={activeIndex} onHover={setActive} onPick={insertMention} />
			)}
		</span>
	);
}

function renderLayer(text: string, tokens: readonly Token[]) {
	const sorted = [...tokens].sort((a, b) => a.start - b.start);
	const parts: React.ReactNode[] = [];
	let cursor = 0;
	sorted.forEach((token, i) => {
		if (token.start < cursor) {
			return;
		}
		parts.push(text.slice(cursor, token.start));
		parts.push(
			<span key={`${token.id}-${i}`} className="mention">
				{token.label}
			</span>
		);
		cursor = token.end;
	});
	parts.push(text.slice(cursor));
	return parts;
}

const SUGGEST_LIMIT = 8;

/**
 * Поиск по локальной реплике — мгновенный, без сети.
 *
 * Совпадения с начала заголовка идут первыми: набирая `[[отч`, человек метит в «Отчёт»,
 * а не в «Годовой отчёт», и первый же пункт должен быть тем, что он и вставит `Enter`ом.
 * Обе очереди набираются до предела по отдельности — иначе точное совпадение,
 * встреченное поздно, не попало бы в выдачу вовсе.
 */
function useMentionResults(query: string | null): { id: string; title: string }[] {
	const store = useStore();
	return useMemo(() => {
		if (query === null) {
			return [];
		}
		const q = query.trim().toLowerCase();
		const prefix: { id: string; title: string }[] = [];
		const rest: { id: string; title: string }[] = [];
		for (const node of store.getSnapshot().replica.nodes.values()) {
			if (node.archivedAt !== null || node.title.trim() === "") {
				continue;
			}
			const title = node.title.toLowerCase();
			if (q === "" || title.startsWith(q)) {
				prefix.push({ id: node.id, title: node.title });
			} else if (title.includes(q)) {
				rest.push({ id: node.id, title: node.title });
			}
			if (prefix.length >= SUGGEST_LIMIT && rest.length >= SUGGEST_LIMIT) {
				break;
			}
		}
		return [...prefix, ...rest].slice(0, SUGGEST_LIMIT);
	}, [query, store]);
}

function MentionSuggest({
	results,
	active,
	onHover,
	onPick
}: {
	results: readonly { id: string; title: string }[];
	active: number;
	onHover: (index: number) => void;
	onPick: (id: string, label: string) => void;
}) {
	if (results.length === 0) {
		return null;
	}

	return (
		<ul className="popover absolute top-full left-0 z-30 mt-1 min-w-64 p-[5px]">
			{results.map((r, i) => (
				<li key={r.id}>
					{/*
					 * Пункт под клавиатурой подсвечен тем же фоном, что и под мышью: наведение
					 * не спорит с выбором, а переносит его на себя — курсор и стрелки всегда
					 * показывают ОДИН активный пункт.
					 */}
					<button
						type="button"
						// Фокус остаётся в поле, поэтому активный пункт помечается
						// `aria-current`, а не фокусом.
						aria-current={i === active}
						className={`block w-full truncate rounded-sm px-3 py-[5px] text-left text-xs font-light transition-colors ${i === active ? "bg-active" : ""}`}
						onMouseMove={() => onHover(i)}
						onMouseDown={(e) => {
							e.preventDefault();
							onPick(r.id, r.title);
						}}
					>
						<span className="text-accent">↗</span> {r.title}
					</button>
				</li>
			))}
		</ul>
	);
}
